"use client";

import { useCallback, useState } from "react";
import { useRouter } from "next/navigation";
import { SpotToast } from "@/components/SpotToast";
import { AdminOAuthSetupRequiredDialog } from "@/components/MCP/AdminOAuthSetupRequiredDialog";
import {
  startOAuthFlow,
  getAdminOAuthSetupRequiredDetails,
  oauthStartErrorMessage,
  type AdminOAuthSetupRequiredDetails,
} from "./utils";
import type { IntegrationInfo, ProviderInfo } from "./types";

/** Wraps `startOAuthFlow` + the admin-setup-required fallback dialog so the
 *  three call sites (AddIntegrationModal, ExternalLinkedAccountCard,
 *  MCPCustomConnectDialog future use) don't each reimplement the
 *  catch-detect-show-dialog dance.
 *
 *  Render `adminSetupDialog` somewhere near the top of your component; call
 *  `start({ integration, provider, ... })` from a click handler. The hook
 *  navigates to the authorize URL on success and shows either an inline
 *  toast or the admin-setup dialog on failure.
 */
export function useStartIntegrationOAuth({
  groupId,
  returnTo,
}: {
  groupId: string;
  returnTo: string;
}) {
  const router = useRouter();
  const [connecting, setConnecting] = useState<string | null>(null);
  const [adminSetupRequired, setAdminSetupRequired] =
    useState<AdminOAuthSetupRequiredDetails | null>(null);

  const start = useCallback(
    async ({
      integration,
      provider,
      credentialId,
      connectionConfig,
      onError,
    }: {
      integration: IntegrationInfo;
      provider: ProviderInfo;
      credentialId?: string;
      connectionConfig?: Record<string, unknown>;
      onError?: () => void;
    }): Promise<boolean> => {
      const key = `${integration.type}::${provider.key}`;
      setConnecting(key);
      try {
        const url = await startOAuthFlow(
          groupId,
          integration,
          provider,
          returnTo,
          credentialId,
          connectionConfig
        );
        router.push(url);
        return true;
      } catch (err) {
        setConnecting(null);
        onError?.();
        const adminDetails = getAdminOAuthSetupRequiredDetails(err);
        if (adminDetails) {
          setAdminSetupRequired(adminDetails);
          return false;
        }
        SpotToast.error(oauthStartErrorMessage(err));
        return false;
      }
    },
    [groupId, returnTo, router]
  );

  const adminSetupDialog = (
    <AdminOAuthSetupRequiredDialog
      open={adminSetupRequired !== null}
      onClose={() => setAdminSetupRequired(null)}
      details={adminSetupRequired}
    />
  );

  return { start, connecting, adminSetupDialog };
}
